import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import { getShiftCoverageSummary } from 'features/shiftCoverage/shiftCoverageSlice'
import { IShiftCoverageShiftResponse } from 'types/api/shiftCoverageApi.interface'
import { IShiftCoverageSiteSummary } from 'types/shiftCoverage.interface'
import { Button } from 'components/ui/Button/Button'
import {
  CoverageWrap,
  SubtitleRow,
  Subtitle,
  ToggleRow,
  ToggleBtn,
  SiteSection,
  SiteHeader,
  SiteName,
  SiteAgg,
  ShiftList,
  ShiftRow,
  ShiftHead,
  ActiveDot,
  ShiftName,
  ShiftWindow,
  CritPill,
  Numbers,
  Chevron,
  CoverageTrack,
  CoverageFill,
  ConfirmedTick,
  SubLine,
  DeptList,
  DeptRow,
  DeptName,
  DeptTrack,
  GapLine,
  HiddenLine,
  LegendRow,
  LegendItem,
  LegendSwatch,
  EmptyState,
  EmptyCta,
  SkeletonRow,
  ErrorWrap,
} from './ShiftCoverageWidget.styled'
import { tokens } from 'styling/tokens'
import { coverageBarPct, coverageTone } from 'utils/coverageStatus'

const subLineFor = (shift: IShiftCoverageShiftResponse): { text: string; tone?: 'danger' | 'info' } => {
  if (!shift.required) return { text: 'no requirements set', tone: 'info' }
  if (shift.phase === 'active') {
    return { text: `checked-in · ${Math.round((shift.covered / shift.required) * 100)}%` }
  }
  if (shift.phase === 'future') {
    return { text: `${shift.confirmed ?? 0} confirmed · starts ${shift.windowFrom}` }
  }
  return { text: 'closed' }
}

const siteAggregate = (site: IShiftCoverageSiteSummary) => {
  const real = site.shifts.filter((s) => s.required > 0)
  const required = real.reduce((a, s) => a + s.required, 0)
  const covered = real.reduce((a, s) => a + s.covered, 0)
  const criticalGap = real.some((s) => s.criticalGap)
  return { required, covered, criticalGap }
}

export const ShiftCoverageWidget = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { summaries, isLoading, hasError } = useSelector((state: RootState) => state.shiftCoverage)
  const [view, setView] = useState<'upcoming' | 'all'>('upcoming')
  const [openSites, setOpenSites] = useState<{ [siteId: number]: boolean }>({})
  const [openHidden, setOpenHidden] = useState<{ [siteId: number]: boolean }>({})
  const [openRows, setOpenRows] = useState<{ [key: string]: boolean }>({})

  const load = () => dispatch(getShiftCoverageSummary({ date: new Date().toISOString().slice(0, 10) }))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  if (isLoading && !summaries.length) {
    return (
      <CoverageWrap>
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </CoverageWrap>
    )
  }

  if (hasError && !summaries.length) {
    return (
      <ErrorWrap>
        Couldn’t load shift coverage.
        <Button variant='secondary' size='sm' onClick={load}>
          Retry
        </Button>
      </ErrorWrap>
    )
  }

  if (!summaries.length) return null

  const anyContent = summaries.some((s) => s.shifts.length || s.shiftsWithoutRequirements.length)
  const anyModels = summaries.some((s) => s.shifts.length)

  if (!anyContent) {
    return (
      <EmptyState>
        No shifts configured yet.
        <EmptyCta onClick={() => navigate('/hr/schedule')}>Set up shifts →</EmptyCta>
      </EmptyState>
    )
  }

  if (!anyModels) {
    return (
      <EmptyState>
        No coverage models yet.
        <EmptyCta onClick={() => navigate('/hr/schedule/coverage-models')}>
          Set required staffing per department →
        </EmptyCta>
      </EmptyState>
    )
  }

  const toggleSite = (siteId: number, fallback: boolean) =>
    setOpenSites((prev) => ({ ...prev, [siteId]: !(prev[siteId] ?? fallback) }))

  const toggleRow = (key: string) => setOpenRows((prev) => ({ ...prev, [key]: !prev[key] }))

  return (
    <CoverageWrap>
      <SubtitleRow>
        <Subtitle>Today · bars show coverage vs required · tick = confirmed</Subtitle>
      </SubtitleRow>

      <ToggleRow>
        <ToggleBtn $active={view === 'upcoming'} onClick={() => setView('upcoming')}>
          Current & upcoming
        </ToggleBtn>
        <ToggleBtn $active={view === 'all'} onClick={() => setView('all')}>
          All shifts today
        </ToggleBtn>
      </ToggleRow>

      {summaries.map((site, index) => {
        const agg = siteAggregate(site)
        const aggTone = coverageTone(agg)
        const isOpen = openSites[site.siteNodeId] ?? index === 0
        const visible = view === 'upcoming' ? site.shifts.filter((s) => s.phase !== 'past') : site.shifts
        const hiddenShown = !!openHidden[site.siteNodeId]
        return (
          <SiteSection key={site.siteNodeId}>
            <SiteHeader type='button' onClick={() => toggleSite(site.siteNodeId, index === 0)}>
              <Chevron $open={isOpen}>›</Chevron>
              <SiteName>{site.siteName}</SiteName>
              <SiteAgg>
                {agg.criticalGap && <CritPill>CRIT</CritPill>}
                <Numbers $tone={aggTone}>
                  {agg.covered}/{agg.required}
                </Numbers>
              </SiteAgg>
            </SiteHeader>

            {isOpen && (
              <>
                {!site.shifts.length ? (
                  <SubLine $tone='info'>No coverage models on this site yet.</SubLine>
                ) : (
                  <ShiftList>
                    {visible.map((shift) => {
                      const tone = coverageTone(shift)
                      const pct = coverageBarPct(shift.covered, shift.required)
                      const sub = subLineFor(shift)
                      const rowKey = `${site.siteNodeId}-${shift.shiftId}`
                      const open = !!openRows[rowKey]
                      const confirmedPct =
                        shift.phase === 'future' && shift.confirmed != null && shift.required
                          ? Math.min(Math.round((shift.confirmed / shift.required) * 100), 100)
                          : null
                      return (
                        <ShiftRow
                          key={shift.shiftId}
                          type='button'
                          $dim={shift.phase === 'past'}
                          onClick={() => toggleRow(rowKey)}
                        >
                          <ShiftHead>
                            {shift.phase === 'active' && <ActiveDot />}
                            <ShiftName>{shift.name}</ShiftName>
                            <ShiftWindow>
                              {shift.windowFrom}–{shift.windowTo}
                            </ShiftWindow>
                            {shift.criticalGap && <CritPill>CRIT</CritPill>}
                            <Numbers $tone={tone}>
                              {shift.covered}/{shift.required}
                            </Numbers>
                            <Chevron $open={open}>›</Chevron>
                          </ShiftHead>
                          <CoverageTrack>
                            <CoverageFill $pct={pct} $tone={tone} />
                            {confirmedPct != null && <ConfirmedTick $pct={confirmedPct} />}
                          </CoverageTrack>
                          <SubLine $tone={sub.tone}>{sub.text}</SubLine>
                          {open && shift.departments.length > 0 && (
                            <DeptList>
                              {shift.departments.map((d) => {
                                const dTone = coverageTone(d)
                                return (
                                  <div key={d.departmentId}>
                                    <DeptRow>
                                      <DeptName>{d.name}</DeptName>
                                      <DeptTrack>
                                        <CoverageFill $pct={coverageBarPct(d.covered, d.required)} $tone={dTone} />
                                      </DeptTrack>
                                      <Numbers $tone={dTone}>
                                        {d.covered}/{d.required}
                                      </Numbers>
                                    </DeptRow>
                                    {d.gapLabel && <GapLine>⚠ {d.gapLabel}</GapLine>}
                                  </div>
                                )
                              })}
                            </DeptList>
                          )}
                        </ShiftRow>
                      )
                    })}
                    {hiddenShown &&
                      site.shiftsWithoutRequirements.map((s) => (
                        <ShiftRow key={`nr-${s.shiftId}`} type='button' $dim>
                          <ShiftHead>
                            <ShiftName>{s.name}</ShiftName>
                            <ShiftWindow>no requirements today</ShiftWindow>
                          </ShiftHead>
                        </ShiftRow>
                      ))}
                  </ShiftList>
                )}

                {site.shiftsWithoutRequirements.length > 0 && (
                  <HiddenLine
                    onClick={() =>
                      setOpenHidden((prev) => ({ ...prev, [site.siteNodeId]: !prev[site.siteNodeId] }))
                    }
                  >
                    {site.shiftsWithoutRequirements.length} shift
                    {site.shiftsWithoutRequirements.length === 1 ? '' : 's'} without requirements today —{' '}
                    <span>{hiddenShown ? 'hide' : 'show'}</span>
                  </HiddenLine>
                )}
              </>
            )}
          </SiteSection>
        )
      })}

      <LegendRow>
        <LegendItem>
          <LegendSwatch $color={tokens.status.success.fg} /> covered
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={tokens.status.warning.fg} /> under target
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={tokens.status.danger.fg} /> critical gap
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={tokens.accent.base} $glow /> active now
        </LegendItem>
      </LegendRow>
    </CoverageWrap>
  )
}
